import { useState, useMemo, Fragment, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useRequests } from '../hooks/useRequests'
import { useCarriers } from '../hooks/useCarriers'
import { useNotification } from '../contexts/NotificationContext'
import { parseApiError } from '../utils/errorUtils'
import { Skeleton, RFQTableSkeleton } from '../components/skeleton'
import { requestService } from '../services/requestService'
import { generateQuotePDF } from '../utils/pdfGenerator'
import './RFQList.css'

function RFQList() {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [expandedRow, setExpandedRow] = useState(null)
  const [sortField, setSortField] = useState('created_at')
  const [sortDir, setSortDir] = useState('desc')
  const [clarificationText, setClarificationText] = useState({}) // keyed by rfq.id
  const [sendingClarification, setSendingClarification] = useState({})
  const [markingOffline, setMarkingOffline] = useState({})
  const { showSuccess, showError } = useNotification()

  // Request data from API
  const {
    requests,
    isLoading,
    error,
    pagination,
    refetch,
    silentRefetch,
    updateStatus,
    loadMore,
    confirmSend,
    markSeen,
  } = useRequests(undefined, sortField, sortDir)

  const [confirmingSend, setConfirmingSend] = useState({})

  // Fetch carriers for ID → name lookup
  const { carriers } = useCarriers()

  // Create carrier ID → name lookup map
  const carrierNameMap = useMemo(() => {
    const map = {}
    carriers.forEach(carrier => {
      map[carrier.id] = carrier.name
    })
    return map
  }, [carriers])

  // Full carrier detail map for PDF generation
  const carrierDetailMap = useMemo(() => {
    const map = {}
    carriers.forEach(carrier => {
      map[carrier.id] = carrier
    })
    return map
  }, [carriers])

  // Enrich requests with carrier names
  const allRFQs = useMemo(() => {
    if (!requests) return []
    return requests.map(rfq => ({
      ...rfq,
      carrierName: carrierNameMap[rfq.carrier] || null
    }))
  }, [requests, carrierNameMap])

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDir('desc')
    }
  }

  const sortIcon = (field) => {
    if (sortField !== field) return ' ↕'
    return sortDir === 'asc' ? ' ↑' : ' ↓'
  }

  // Background polling — silent refetch every 4s, only updates changed rows
  useEffect(() => {
    const timerId = setInterval(silentRefetch, 4000)
    return () => clearInterval(timerId)
  }, [silentRefetch])

  const filteredRFQs = useMemo(() => {
    return allRFQs.filter(rfq =>
      rfq.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rfq.carrier?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rfq.carrierName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rfq.origin?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rfq.destination?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rfq.status?.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [allRFQs, searchTerm])

  const toggleRow = (id) => {
    const opening = expandedRow !== id
    setExpandedRow(opening ? id : null)
    // Optimistically clear the NEW badge locally AND fire the backend POST
    // in the background. Without the optimistic step the badge would
    // re-appear after the user collapses the row, until the next
    // silentRefetch (up to 15s).
    if (opening) {
      markSeen(id)
    }
  }

  // A request is "unseen" by the current user when its current rawStatus
  // differs from the status they last saw (or when no view record exists at
  // all). Suppressed while the row is open — the user is actively reading it.
  const isUnseen = (rfq) => {
    if (!rfq) return false
    if (expandedRow === rfq.id) return false
    if (!rfq.lastSeenStatus) return true
    return rfq.lastSeenStatus !== rfq.rawStatus
  }

  const isAIPending = (rfq) =>
    !rfq.origin || rfq.origin.includes('Origin City') || rfq.origin.includes('Unknown')

  const getStatusClass = (status) => {
    if (status === 'Ready') return 'status-ready'
    if (status === 'Awaiting Response') return 'status-awaiting'
    if (status === 'Accepted') return 'status-confirmed'
    if (status === 'Cancelled') return 'status-cancelled'
    if (status === 'Not Accepted') return 'status-declined'
    if (status === 'Needs Clarification') return 'status-clarification'
    if (status === 'Failed') return 'status-failed'
    if (status === 'Review Required') return 'status-review'
    if (status === 'Offline Follow-up') return 'status-offline'
    return 'status-processing'
  }

  const handleSendClarification = async (rfq) => {
    const notes = clarificationText[rfq.id]?.trim()
    if (!notes) return

    setSendingClarification(prev => ({ ...prev, [rfq.id]: true }))
    try {
      await requestService.sendClarification(rfq.id, notes)
      showSuccess(`Clarification sent to carrier for ${rfq.id.substring(0, 8)}…`)
      setClarificationText(prev => ({ ...prev, [rfq.id]: '' }))
      refetch()
    } catch (err) {
      const parsed = parseApiError(err)
      showError(parsed.message)
    } finally {
      setSendingClarification(prev => ({ ...prev, [rfq.id]: false }))
    }
  }

  const handleMarkOfflineFollowup = async (rfq) => {
    if (!window.confirm(
      `Continue this RFQ's conversation with the carrier offline?\n\n` +
      `Any further carrier emails on RFQ ${rfq.id.substring(0, 8)}… will receive an automated handoff reply and will not appear in this app.`
    )) {
      return
    }
    setMarkingOffline(prev => ({ ...prev, [rfq.id]: true }))
    try {
      await requestService.markOfflineFollowup(rfq.id)
      showSuccess(`RFQ ${rfq.id.substring(0, 8)}… moved to offline follow-up`)
      refetch()
    } catch (err) {
      const parsed = parseApiError(err)
      showError(parsed.message)
    } finally {
      setMarkingOffline(prev => ({ ...prev, [rfq.id]: false }))
    }
  }

  const handleAcceptQuote = async (rfq) => {
    try {
      await updateStatus(rfq.id, 'accepted')
      showSuccess(`Quote for ${rfq.id} accepted!`)
    } catch (err) {
      console.error('[RFQList] Accept failed:', err)
      const parsed = parseApiError(err)
      showError(parsed.message)
    }
  }

  const handleDeclineQuote = async (rfq) => {
    if (!window.confirm(`Are you sure you want to decline the quote for ${rfq.id}?`)) {
      return
    }
    try {
      await updateStatus(rfq.id, 'cancelled')
      showSuccess(`Quote for ${rfq.id} declined`)
    } catch (err) {
      console.error('[RFQList] Decline failed:', err)
      const parsed = parseApiError(err)
      showError(parsed.message)
    }
  }

  const handleCancelRequest = async (rfq) => {
    if (!window.confirm(`Are you sure you want to cancel request ${rfq.id}?`)) {
      return
    }
    try {
      await updateStatus(rfq.id, 'cancelled')
      showSuccess(`Request ${rfq.id} cancelled`)
    } catch (err) {
      console.error('[RFQList] Cancel failed:', err)
      const parsed = parseApiError(err)
      showError(parsed.message)
    }
  }

  const handleConfirmSend = async (rfq) => {
    setConfirmingSend(prev => ({ ...prev, [rfq.id]: true }))
    try {
      await confirmSend(rfq.id, rfq.fullDescription)
      showSuccess(`Request sent to carrier for ${rfq.id.substring(0, 8)}…`)
    } catch (err) {
      const apiErrors = err.response?.data?.errors
      const apiMessage = err.response?.data?.message
      const parsed = parseApiError(err)
      const errorMsg = apiErrors?.length ? apiErrors.join('; ') : apiMessage || parsed.message
      showError(errorMsg)
    } finally {
      setConfirmingSend(prev => ({ ...prev, [rfq.id]: false }))
    }
  }

  const handleDownloadQuote = (rfq) => {
    const carrier = carrierDetailMap[rfq.carrier] || null
    generateQuotePDF(rfq, carrier)
  }

  // Loading state - show skeleton layout
  if (isLoading) {
    return (
      <div className="rfq-list-page">
        <div className="page-header">
          <div className="header-content">
            <h1>My Requests</h1>
            <p>Track and manage all your freight quotes in one place</p>
          </div>
          <Skeleton variant="rectangular" width="145px" height="48px" />
        </div>

        <div className="search-filter-section">
          <div className="search-bar">
            <Skeleton variant="rectangular" width="100%" height="52px" />
          </div>
          <Skeleton variant="text" width="180px" height="0.875rem" />
        </div>

        <RFQTableSkeleton rows={5} />
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="rfq-list-page">
        <div className="error-state">
          <span className="error-icon">⚠️</span>
          <h3>Failed to load requests</h3>
          <p>{error.message}</p>
          <button className="btn-retry" onClick={refetch}>
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="rfq-list-page">
      <div className="page-header">
        <div className="header-content">
          <h1>My Requests</h1>
          <p>Track and manage all your freight quotes in one place</p>
        </div>
        <button className="btn-new-rfq" onClick={() => navigate('/')}>
          + New Request
        </button>
      </div>

      <div className="search-filter-section">
        <div className="search-bar">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search by Request ID, carrier, route, or status..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="results-count">
          Showing {filteredRFQs.length} of {allRFQs.length} requests
        </div>
      </div>

      <div className="table-container">
        {filteredRFQs.length > 0 ? (
          <table className="rfq-table">
            <thead>
              <tr>
                <th>Request ID</th>
                <th className="th-sortable" onClick={() => handleSort('carrier')}>Carrier{sortIcon('carrier')}</th>
                <th>Route</th>
                <th>Shipment Type</th>
                <th>Status</th>
                <th className="th-sortable" onClick={() => handleSort('created_at')}>Submitted{sortIcon('created_at')}</th>
                <th className="th-sortable" onClick={() => handleSort('price')}>Price{sortIcon('price')}</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRFQs.map(rfq => (
                <Fragment key={rfq.id}>
                  <tr className={expandedRow === rfq.id ? 'expanded' : ''}>
                    <td className="rfq-id-cell">
                      <span className="rfq-id-short" title={rfq.id}>{(rfq.id || '').substring(0, 8)}…</span>
                    </td>
                    <td>{rfq.carrierName || rfq.carrier || 'Unassigned'}</td>
                    <td className="route-cell">
                      {isAIPending(rfq) ? (
                        <span className="ai-processing-pill">Being processed…</span>
                      ) : (
                        <div className="route-compact">
                          <span>{rfq.origin}</span>
                          <span className="route-arrow">→</span>
                          <span>{rfq.destination}</span>
                        </div>
                      )}
                    </td>
                    <td>
                      {rfq.shipmentType || (isAIPending(rfq)
                        ? <span className="ai-processing-pill">Being processed…</span>
                        : <span className="text-muted">—</span>)}
                    </td>
                    <td>
                      <span className={`status-badge ${getStatusClass(rfq.status)}`}>
                        {rfq.status}
                      </span>
                      {isUnseen(rfq) && (
                        <span className="badge-new" title="Updated since you last viewed this request">NEW</span>
                      )}
                    </td>
                    <td className="submitted-cell">{rfq.submittedDate}</td>
                    <td className="price-cell">
                      {rfq.quotedPrice != null ? (
                        <span className="price-value">
                          <strong>{rfq.quotedPriceFormatted}</strong>
                          {rfq.quoteIncludesGST === true && <span className="gst-label gst-inclusive">incl. GST</span>}
                          {rfq.quoteIncludesGST === false && <span className="gst-label gst-exclusive">ex. GST</span>}
                        </span>
                      ) : rfq.status === 'Not Accepted' ? (
                        <span className="text-muted">—</span>
                      ) : rfq.status === 'Cancelled' ? (
                        <span className="text-muted">—</span>
                      ) : rfq.status === 'Failed' ? (
                        <span className="text-muted">—</span>
                      ) : (
                        <span className="pending-text">Pending</span>
                      )}
                    </td>
                    <td className="actions-cell">
                      <button
                        className="btn-details"
                        onClick={() => toggleRow(rfq.id)}
                      >
                        {expandedRow === rfq.id ? '▼ Hide' : '▶ Details'}
                      </button>
                    </td>
                  </tr>
                  {expandedRow === rfq.id && (
                    <tr className="details-row">
                      <td colSpan="8">
                        <div className="rfd">
                          {/* LEFT COLUMN — command panel */}
                          <div className="rfd__left">
                            <div className="rfd__id-row">
                              <span className="rfd__id-label">Request ID</span>
                              <code
                                className="rfd__id-value"
                                title="Click to copy"
                                onClick={() => navigator.clipboard.writeText(rfq.id)}
                              >
                                {rfq.id}
                              </code>
                            </div>

                            {rfq.status === 'Ready' && rfq.quotedPriceFormatted && (
                              <div className="rfd__price">
                                <span className="rfd__price-amount">{rfq.quotedPriceFormatted}</span>
                                {rfq.quoteIncludesGST === true && (
                                  <span className="gst-label gst-inclusive">incl. GST</span>
                                )}
                                {rfq.quoteIncludesGST === false && (
                                  <span className="gst-label gst-exclusive">ex. GST</span>
                                )}
                              </div>
                            )}

                            <div className="rfd__route">
                              <div className="rfd__route-end">
                                <span className="rfd__route-dir">FROM</span>
                                <span className="rfd__route-place">
                                  {isAIPending(rfq)
                                    ? <span className="ai-processing-pill">Processing…</span>
                                    : (rfq.originFull || rfq.origin || '—')}
                                </span>
                                {rfq.forkliftAtOrigin != null && (
                                  <span className="rfd__route-flag">
                                    {rfq.forkliftAtOrigin ? 'Forklift' : 'No forklift'}
                                  </span>
                                )}
                              </div>
                              <div className="rfd__route-arrow" aria-hidden="true">↓</div>
                              <div className="rfd__route-end">
                                <span className="rfd__route-dir">TO</span>
                                <span className="rfd__route-place">
                                  {isAIPending(rfq)
                                    ? <span className="ai-processing-pill">Processing…</span>
                                    : (rfq.destinationFull || rfq.destination || '—')}
                                </span>
                                {rfq.forkliftAtDestination != null && (
                                  <span className="rfd__route-flag">
                                    {rfq.forkliftAtDestination ? 'Forklift' : 'No forklift'}
                                  </span>
                                )}
                              </div>
                            </div>

                            {rfq.status === 'Failed' && (
                              <div className="rfd__alert rfd__alert--danger">
                                <span className="rfd__alert-label">Failure Reason</span>
                                <p className="rfd__alert-text">
                                  {rfq.failureReason || 'This request failed to process.'}
                                </p>
                              </div>
                            )}

                            {rfq.carrierResponseNote && (
                              <div className={`rfd__alert ${rfq.status === 'Not Accepted' ? 'rfd__alert--danger' : 'rfd__alert--warn'}`}>
                                <span className="rfd__alert-label">
                                  {rfq.status === 'Not Accepted' ? 'Carrier Response' : 'Carrier Questions'}
                                </span>
                                <p className="rfd__alert-text">{rfq.carrierResponseNote}</p>
                              </div>
                            )}

                            {rfq.clarificationNotes && (
                              <div className="rfd__alert">
                                <span className="rfd__alert-label">Your Reply</span>
                                <p className="rfd__alert-text">{rfq.clarificationNotes}</p>
                              </div>
                            )}

                            {rfq.status === 'Review Required' && (
                              <div className="rfd__callout rfd__callout--info">
                                <p className="rfd__callout-text">
                                  AI extraction complete. Review details and confirm to send to carrier.
                                </p>
                                <button
                                  className="btn-accept"
                                  onClick={() => handleConfirmSend(rfq)}
                                  disabled={confirmingSend[rfq.id]}
                                >
                                  {confirmingSend[rfq.id] ? 'Sending…' : 'Confirm & Send'}
                                </button>
                              </div>
                            )}

                            {rfq.status === 'Needs Clarification' && !rfq.clarificationReplySent && (
                              <div className="rfd__callout rfd__callout--warn">
                                <p className="rfd__callout-label">Reply to Carrier</p>
                                <textarea
                                  className="rfd__textarea"
                                  placeholder="Answer the carrier's questions…"
                                  value={clarificationText[rfq.id] || ''}
                                  onChange={(e) =>
                                    setClarificationText(prev => ({ ...prev, [rfq.id]: e.target.value }))
                                  }
                                  rows={3}
                                />
                                <button
                                  className="btn-send-clarification"
                                  onClick={() => handleSendClarification(rfq)}
                                  disabled={!clarificationText[rfq.id]?.trim() || sendingClarification[rfq.id]}
                                >
                                  {sendingClarification[rfq.id] ? 'Sending…' : 'Send Reply'}
                                </button>
                              </div>
                            )}

                            {rfq.status === 'Needs Clarification' && rfq.clarificationReplySent && (
                              <div className="rfd__callout rfd__callout--warn">
                                <p className="rfd__callout-label">In-app reply used</p>
                                <p className="rfd__callout-text">
                                  You've already replied once on this RFQ. For any further questions, please contact the carrier directly through your usual channel.
                                </p>
                                <button
                                  className="btn-send-clarification"
                                  onClick={() => handleMarkOfflineFollowup(rfq)}
                                  disabled={markingOffline[rfq.id]}
                                >
                                  {markingOffline[rfq.id] ? 'Updating…' : 'Continue with carrier offline'}
                                </button>
                              </div>
                            )}

                            <div className="rfd__actions">
                              {rfq.status === 'Ready' && (
                                <>
                                  <button className="btn-cancel" onClick={() => handleDeclineQuote(rfq)}>
                                    Decline
                                  </button>
                                  <button className="btn-accept" onClick={() => handleAcceptQuote(rfq)}>
                                    Accept Quote
                                  </button>
                                </>
                              )}
                              {rfq.status === 'Accepted' && (
                                <button className="btn-download-quote" onClick={() => handleDownloadQuote(rfq)}>
                                  Download Quote
                                </button>
                              )}
                              {rfq.status !== 'Cancelled' &&
                                rfq.status !== 'Accepted' &&
                                rfq.status !== 'Ready' &&
                                rfq.status !== 'Not Accepted' &&
                                rfq.status !== 'Needs Clarification' &&
                                rfq.status !== 'Failed' &&
                                rfq.status !== 'Review Required' &&
                                rfq.status !== 'Offline Follow-up' && (
                                  <button className="btn-cancel" onClick={() => handleCancelRequest(rfq)}>
                                    Cancel Request
                                  </button>
                                )}
                            </div>
                          </div>

                          {/* RIGHT COLUMN — detail log */}
                          <div className="rfd__right">
                            <dl className="rfd__dl">
                              <dt>Status</dt>
                              <dd>{rfq.status}</dd>
                              {rfq.quotedPriceFormatted && (
                                <>
                                  <dt>Quote Price</dt>
                                  <dd className="rfd__dd--price">
                                    {rfq.quotedPriceFormatted}
                                    {rfq.quoteIncludesGST === true && <span className="gst-label gst-inclusive">incl. GST</span>}
                                    {rfq.quoteIncludesGST === false && <span className="gst-label gst-exclusive">ex. GST</span>}
                                  </dd>
                                </>
                              )}
                              {rfq.shipmentType && (<><dt>Type</dt><dd>{rfq.shipmentType}</dd></>)}
                              {rfq.commodityType && (<><dt>Commodity</dt><dd>{rfq.commodityType}</dd></>)}
                              {rfq.containerType && (<><dt>Container</dt><dd>{rfq.containerType}</dd></>)}
                              {rfq.weightKg != null && (<><dt>Weight</dt><dd>{rfq.weightKg.toLocaleString()} kg</dd></>)}
                              {rfq.volumeCBM != null && (<><dt>Volume</dt><dd>{rfq.volumeCBM} CBM</dd></>)}
                              {rfq.quantity != null && (<><dt>Quantity</dt><dd>{rfq.quantity} × {rfq.packageType || 'packages'}</dd></>)}
                              {(rfq.lengthCM != null && rfq.widthCM != null && rfq.heightCM != null) && (
                                <><dt>Dimensions</dt><dd>{rfq.lengthCM} × {rfq.widthCM} × {rfq.heightCM} cm</dd></>
                              )}
                              {rfq.incoterms && (<><dt>Incoterms</dt><dd>{rfq.incoterms}</dd></>)}
                              {rfq.dangerousGoods != null && (
                                <><dt>Dangerous Goods</dt>
                                <dd className={rfq.dangerousGoods ? 'rfd__dd--warn' : ''}>
                                  {rfq.dangerousGoods ? 'Yes' : 'No'}
                                </dd></>
                              )}
                              {rfq.stackable != null && (<><dt>Stackable</dt><dd>{rfq.stackable ? 'Yes' : 'No'}</dd></>)}
                              {rfq.readyDate && (<><dt>Ready Date</dt><dd>{rfq.readyDate}</dd></>)}
                              {rfq.deadline && (<><dt>Deadline</dt><dd>{rfq.deadline}</dd></>)}
                              {rfq.transitDays && (<><dt>Transit Time</dt><dd>{rfq.transitDays} days</dd></>)}
                              {rfq.validUntil && (<><dt>Valid Until</dt><dd>{rfq.validUntil}</dd></>)}
                            </dl>

                            {rfq.quoteTerms && (
                              <div className="rfd__prose-field">
                                <span className="rfd__prose-label">Terms</span>
                                <p className="rfd__prose-text">{rfq.quoteTerms}</p>
                              </div>
                            )}

                            {rfq.specialRequirements && (
                              <div className="rfd__prose-field">
                                <span className="rfd__prose-label">Special Requirements</span>
                                <p className="rfd__prose-text">{rfq.specialRequirements}</p>
                              </div>
                            )}

                            {(rfq.aiDescription || rfq.fullDescription) && (
                              <div className="rfd__prose-field">
                                <span className="rfd__prose-label">Original RFQ Text</span>
                                <pre className="rfd__pre">{rfq.aiDescription || rfq.fullDescription}</pre>
                              </div>
                            )}

                            {rfq.quoteResponseText && (
                              <div className="rfd__prose-field">
                                <span className="rfd__prose-label">Carrier Reply</span>
                                <pre className="rfd__pre">{rfq.quoteResponseText}</pre>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="empty-state">
            <span className="empty-icon">📭</span>
            <h3>No requests found</h3>
            <p>{allRFQs.length === 0 ? 'Submit your first RFQ to get started' : 'Your search didn\'t match any requests'}</p>
            {allRFQs.length === 0 && (
              <button className="btn-new-rfq" onClick={() => navigate('/')}>
                + New Request
              </button>
            )}
          </div>
        )}
      </div>

      {/* Load More Button */}
      {pagination.hasMore && (
        <div className="load-more-container">
          <button className="btn-load-more" onClick={loadMore}>
            Load More
          </button>
        </div>
      )}

    </div>
  )
}

export default RFQList
